import {
  Choose,
  If,
  Otherwise,
  When,
} from '@/utility_components/ControlStatements'
import { storeBookingDetailsForV2 } from '@app/bookings/utils/bookingHelper'
import { SlotAvailabilityType } from 'app/Bookings/constants'
import { validateAndProceedBooking } from 'app/Bookings/utils'
import { openProviderProfileShowAllSlotsInUrl } from 'app/ProviderDetails/profileShowAllSlotsUrl'
import {
  getPreviousRouterUrlQuery,
  providerRudderStackEvent,
  queryFilters,
  setTrackProviderInformation,
} from 'app/ProviderDetails/utils'
import {
  AvailabilityFilterValues,
  LabelAppointmentMapping,
  SLOT_LOCATION_MAP,
} from 'app/ProviderSearch/constants'
import { LinkElement } from 'app/ProviderSearch/LeadGenerationElements'
import { getSelectedPaymentType } from 'app/ProviderSearch/rudderEventUtils'
import {
  generateDatedSlots,
  generateDates,
  groupFilters,
  isSlotOutsideBookingWindow,
} from 'app/ProviderSearch/utils'
import classNames from 'classnames'
import DatePaginator from 'common/DatePaginator'
import PhoneNumberLink from 'common/PhoneNumberLink'
import SlotGrid from 'common/SlotGrid'
import TimeZonePicker from 'common/TimeZonePicker/V3'
import VisitTypeCheckbox from 'common/VisitTypeCheckbox'
import { Button, Typography } from 'component_library/1_Atoms'
import {
  CTA_CLASSES,
  INTAKE_BOOKING_WINDOW_HOURS,
  providerProfileNumber,
  RudderstackIntegrations,
} from 'constants/common'
import {
  PRIMARY_BOOK_BUTTON_DEVICE,
  PRIMARY_BOOK_BUTTON_TRACKING,
} from 'constants/primaryBookButton'
import { RudderEvent } from 'constants/rudderEvents'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import ProviderSlotContext from 'context/ProviderBooking/ProviderSlots'
import SlotDateContext from 'context/ProviderBooking/SlotDate'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { addDays, differenceInCalendarDays, set } from 'date-fns'
import withErrorBoundary from 'hoc/withErrorBoundary'
import withTimezoneInitialized from 'hoc/withTimezoneInitialized'
import useLeadGenSubmission, {
  LeadGenScenarios,
} from 'hooks/provider/useLeadGenSubmission'
import getNextOpeningSlot from 'hooks/slots/getNextOpeningSlot'
import usePrimaryBookButtonSelection from 'hooks/slots/usePrimaryBookButtonSelection'
import usePrimaryBookButtonProfileV2 from 'hooks/treatments/usePrimaryBookButtonProfileV2'
import useAppSelector from 'hooks/useAppSelector'
import useFetchTimezoneFromLocation from 'hooks/useFetchTimezoneFromLocation'
import useThriveAuth from 'hooks/useThriveAuth'
import { useRouter } from 'next/router'
import type { CSSProperties } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  cleanBookingInfo,
  startBooking,
  updateBookingDetails,
} from 'state/booking/actions'
import { initialBookingHelperState } from 'state/booking/constants'
import {
  clearNonPersistingDataInSession,
  countProviderSlots,
  filterDateSlotsByAppointmentMode,
  getHasFutureSlots,
  getParsedQuery,
  modifyURLQuery,
} from 'utils/common'
import logEvent from 'utils/logEvent'
import { readTimezone } from 'utils/timezoneStorage'
import PrimaryBookButton from '../PrimaryBookButton'
import AppointmentDropdown from './AppointmentDropdown'
import { getAvailabilityFilter } from './AppointmentToggle/utils'
import useDynamicSlotRowsAndSticky from './useDynamicSlotRowsAndSticky'
import styles from './index.module.scss'
import { getAvailabilityFilters } from './utils'

/** Sticky profile UX shows three day columns while context may load more for hero/modal. */
const PROFILE_STICKY_VISIBLE_DAY_COUNT = 3

type StickyScheduleSectionProps = {
  providerDetails: Provider
  containerStyle?: CSSProperties
  onCalculateSlots?: (slotsCount: number) => void
  /** Callback when appointment mode (visit type) changes in the sticky section */
  onAppointmentModeChange?: (mode: SlotAvailabilityType) => void
  isExtendedBookingEnabled?: boolean
}

const StickyScheduleSection: React.FC<StickyScheduleSectionProps> = ({
  providerDetails,
  containerStyle,
  onCalculateSlots,
  onAppointmentModeChange,
  isExtendedBookingEnabled = false,
}) => {
  const router = useRouter()
  const {
    isAuthenticated,
    loginWithRedirect,
    user,
    isSupportOrThirdPartyUser,
    isThirdPartyAgent,
  } = useThriveAuth()
  const dispatch = useDispatch()
  const formattedRouterQuery = getParsedQuery(router)
  const { id, filters, practice } = providerDetails ?? {}
  const { latitude, longitude } = practice ?? {}
  const { phoneNumber, formattedPhoneNumber } = providerProfileNumber
  const groupedFilters = filters ? groupFilters(filters) : []

  const { dbUser, userEmail, b2bUserDetails } = useAppSelector(
    (state) => state.bookingHelper
  )

  const { bookingAttribution, salesforceDetails } = useAppSelector(
    (state) => state.global
  )

  const {
    providerSlots,
    loading: slotsLoading,
    error: slotsError,
    nextAvailabilityForProviders,
  } = useContext(ProviderSlotContext)

  const { setStartSlotDate, startSlotDate, slotListLimit } = useContext(
    SlotDateContext
  )

  const { updateTimezone, format, timeZone } = useContext(TimeZoneContext)

  const {
    timeZoneFetchedFromLocation,
    fetchTimeZoneLoading,
  } = useFetchTimezoneFromLocation({
    latitude: latitude?.toString(),
    longitude: longitude?.toString(),
    allowTimezoneAPICall: isSupportOrThirdPartyUser && !readTimezone(),
  })

  const {
    insuranceId,
    selfPay,
    counselingType,
    availabilityType: availabilityTypeId,
    userId,
    patients,
  } = formattedRouterQuery

  const counselingTypeId = Array.isArray(counselingType)
    ? counselingType[0]
    : counselingType

  const {
    availabilityTypeValue,
    availabilityLabels,
    availabilityFilters,
  } = getAvailabilityFilter(availabilityTypeId || [], providerDetails)

  const [appointmentMode, setAppointmentMode] = useState<SlotAvailabilityType>(
    availabilityTypeValue
  )

  const [counselingSelection, setCounselingSelection] = useState({
    value: counselingTypeId as string,
    label: '',
  })
  const [renderReason, setRenderReason] = useState(
    'Provider Profile Availability Section Loads'
  )
  const [pageHasLoaded, setPageHasLoaded] = useState<boolean>(false)
  const bookingWindowHours = INTAKE_BOOKING_WINDOW_HOURS

  const dateSlots: DateSlot = providerSlots[id] || {}
  const filteredDateSlotsByAppointmentMode: DateSlot = !appointmentMode
    ? dateSlots
    : filterDateSlotsByAppointmentMode(dateSlots || {}, appointmentMode)

  const stickyVisibleDayCount = Math.min(
    PROFILE_STICKY_VISIBLE_DAY_COUNT,
    slotListLimit
  )

  const dates: Date[] = generateDates(startSlotDate, stickyVisibleDayCount)
  const generatedDatedSlots = generateDatedSlots(
    dates,
    filteredDateSlotsByAppointmentMode,
    (slot) =>
      isSlotOutsideBookingWindow(slot?.start, bookingWindowHours) ? slot : null
  )
  const userSelectedAvailType = groupedFilters[
    ProviderFilterTypes.AVAILABILITY_TYPE
  ]?.filter((filter) => {
    const availType = formattedRouterQuery?.availabilityType

    if (!availType) {
      return false
    }

    const availTypeId = Array.isArray(availType) ? availType[0] : availType

    return filter?.id === availTypeId
  })
  const providerSlotsCount = countProviderSlots(
    generatedDatedSlots,
    bookingWindowHours
  )
  const providerSlotsCountShown = countProviderSlots(
    generatedDatedSlots,
    bookingWindowHours
  )

  const counselingTypes = groupedFilters[ProviderFilterTypes.COUNSELING_TYPE]
  const counselingTypeValue = counselingTypes?.find((type) => {
    return type.id === counselingTypeId
  })
  const lowAvailabilityCount = 3
  const isLowAvailability =
    !(slotsLoading || slotsError?.message) &&
    providerSlotsCountShown <= lowAvailabilityCount

  const trackSlotSelected = (
    selectedSlot: SelectedSlot,
    slotType: 'primary' | 'normal'
  ) => {
    const selectedTherapyType = counselingTypes?.find(
      (type) => type.id === counselingSelection?.value
    )?.value

    logEvent(
      RudderEvent.PROVIDER_SLOT_SELECTED,
      {
        count_provider_slots_shown: providerSlotsCount,
        slot_timestamp: selectedSlot.slot?.start,
        payment_type_filter: getSelectedPaymentType(
          getPreviousRouterUrlQuery()
        ),
        ...queryFilters(providerDetails, getPreviousRouterUrlQuery()),
        ...setTrackProviderInformation(providerDetails),
        account_id: user?.id,
        therapy_type: selectedTherapyType || null,
        slot_location: SLOT_LOCATION_MAP.providers_profile,
        slot_type: slotType,
        ...(isPrimaryBookButtonV2Enabled && {
          surface: PRIMARY_BOOK_BUTTON_TRACKING.SURFACE,
          device: PRIMARY_BOOK_BUTTON_DEVICE.DESKTOP,
          variant: PRIMARY_BOOK_BUTTON_TRACKING.VARIANT_LABEL,
        }),
      },
      {
        [RudderstackIntegrations.GOOGLE_ADS]: true,
        [RudderstackIntegrations.FACEBOOK_PIXEL]: true,
      }
    )
  }

  const bookAppointment = (selectedSlot: SelectedSlot) => {
    let selectedSlotAppointmentType: SlotAvailabilityType = null

    if (selectedSlot.slot?.availabilityType.length === 1) {
      selectedSlotAppointmentType =
        selectedSlot.slot.availabilityType[0] === SlotAvailabilityType.ONLINE
          ? SlotAvailabilityType.ONLINE
          : SlotAvailabilityType.INPERSON
    } else {
      selectedSlotAppointmentType = SlotAvailabilityType.ONLINE //default to video visit if none selected/ both selected
    }

    const availTypeToAddInRouterUrl = appointmentMode
      ? appointmentMode
      : SlotAvailabilityType.ONLINE

    const availTypeIdToAddInRouterUrl = availabilityFilters?.find(
      (filter) =>
        LabelAppointmentMapping[filter.value] === availTypeToAddInRouterUrl
    )?.id

    dispatch(cleanBookingInfo(isThirdPartyAgent))
    dispatch(
      startBooking({
        ...initialBookingHelperState,
        ...(!!b2bUserDetails && { b2bUserDetails }),
        selectedSlot,
        providerDetails,
        filters: {
          slotAvailabilityTypes: selectedSlot.slot.availabilityType,
        },
        ...(!!dbUser && { dbUser, userEmail: dbUser.email }),
        ...(!!userEmail && { userEmail }), //userEmail will be available if redirecting from Support dashboard.
      })
    )

    dispatch(
      updateBookingDetails({
        providerId: providerDetails.id,
        ...(counselingSelection?.value && {
          counselingTypeId: counselingSelection.value as string,
        }),
        counselingType: counselingTypeValue ? counselingTypeValue.value : '',
        ...(!appointmentMode
          ? { appointmentMode: selectedSlotAppointmentType }
          : { appointmentMode }),
      })
    )

    // Determine the appointment mode to use - prioritize user selection over slot type
    const appointmentModeToUse = appointmentMode || selectedSlotAppointmentType

    // Get availability filters from provider if not available from URL query
    const providerAvailabilityFilters = getAvailabilityFilters(providerDetails)
    const filtersToUse = availabilityFilters?.length
      ? availabilityFilters
      : providerAvailabilityFilters

    // Convert appointment mode to the correct filter value format
    const appointmentModeFilterValue =
      appointmentModeToUse === SlotAvailabilityType.ONLINE
        ? AvailabilityFilterValues.online
        : AvailabilityFilterValues.inPerson

    const slotAppointmentModeId = filtersToUse?.find(
      (filter) => filter.value === appointmentModeFilterValue
    )?.id

    storeBookingDetailsForV2({
      selectedSlot,
      providerDetails,
      appointmentModeId: slotAppointmentModeId,
      counselingTypeId: counselingSelection?.value,
      bookingAttribution,
      salesforceDetails,
    })

    const queryObject = {
      ...(insuranceId && { insuranceId }),
      ...(selfPay && { selfPay }),
      ...(userId && { userId }),
      ...(patients && { patients }),
      ...(availTypeIdToAddInRouterUrl && {
        availabilityType: availTypeIdToAddInRouterUrl,
      }),
      ...(counselingTypeId && { counselingType: counselingSelection?.value }),
      slotId: selectedSlot.slot.id,
    }

    validateAndProceedBooking(
      isAuthenticated,
      loginWithRedirect,
      router,
      queryObject
    )
  }

  const setCounselingSelectionInUrl = (selection) => {
    // update in URL query here.
    modifyURLQuery(
      router,
      {
        counselingType: [selection.value],
        offset: '0',
      },
      false,
      'replace'
    )
  }

  const handleDateChange = (date) => {
    setStartSlotDate(date)
    setRenderReason('Date Options Navigated')
  }

  const handleAppointmentMode = (mode) => {
    setAppointmentMode(mode)
    setRenderReason(`Appointment mode changed to ${mode}`)
    onAppointmentModeChange?.(mode)
    // A slot selected under the previous visit type may not be valid for
    // the new one, so require the user to reconfirm a valid time.
    resetPrimaryBookButtonSelectedSlot()
  }

  const handleCounselingSelection = (selection) => {
    setCounselingSelection(selection)
    setCounselingSelectionInUrl(selection)
    setRenderReason(`Counseling type changed to ${selection.label}`)
    // A slot selected under the previous counseling type may not be valid
    // for the new one, so require the user to reconfirm a valid time.
    resetPrimaryBookButtonSelectedSlot()
  }

  const handleLogin = () => {
    clearNonPersistingDataInSession()
    dispatch(cleanBookingInfo())
    loginWithRedirect()
  }

  const registeredRDSEvent = useRef<boolean>(false)

  const {
    isEnabled: isPrimaryBookButtonV2Enabled,
  } = usePrimaryBookButtonProfileV2()

  const {
    cardRef,
    visibleSlotRows,
    isStickyFeasible,
  } = useDynamicSlotRowsAndSticky(isPrimaryBookButtonV2Enabled)

  const nextOpeningSlot = isPrimaryBookButtonV2Enabled
    ? getNextOpeningSlot(
        filteredDateSlotsByAppointmentMode,
        bookingWindowHours,
        nextAvailabilityForProviders,
        id
      )
    : null

  const {
    effectiveSlot: primaryBookButtonEffectiveSlot,
    isSlotSelected: isPrimaryBookButtonSlotSelected,
    isConfirming: isPrimaryBookButtonConfirming,
    onSlotClick: handlePrimaryBookButtonSlotClick,
    onConfirm: handlePrimaryBookButtonConfirm,
    resetSelectedSlot: resetPrimaryBookButtonSelectedSlot,
  } = usePrimaryBookButtonSelection({
    isEnabled: isPrimaryBookButtonV2Enabled,
    nextOpeningSlot,
    providerDetails,
    // fc-058: dedicated click event for the primary book button, separate
    // from the "Provider Slot Selected" booking event fired inside
    // bookAppointment below.
    onBook: (selectedSlot, slotType) => {
      logEvent(
        RudderEvent.PROVIDER_PRIMARY_BOOK_BUTTON_CLICKED,
        {
          ...setTrackProviderInformation(providerDetails),
          account_id: user?.id,
          surface: PRIMARY_BOOK_BUTTON_TRACKING.SURFACE,
          device: PRIMARY_BOOK_BUTTON_DEVICE.DESKTOP,
          variant: PRIMARY_BOOK_BUTTON_TRACKING.VARIANT_LABEL,
        },
        {
          [RudderstackIntegrations.GOOGLE_ADS]: true,
        }
      )

      // For a manually picked slot, Provider Slot Selected already fired on
      // slot click (trackSlotSelected below). The express path (confirming
      // the pre-loaded slot without picking one) never goes through that
      // click, so fire it here to avoid missing/duplicating the event.
      if (slotType === 'primary') {
        trackSlotSelected(selectedSlot, 'primary')
      }

      bookAppointment(selectedSlot)
    },
  })

  const { forwardingFilters } = useLeadGenSubmission({
    groupedFilters,
    providerId: providerDetails.id,
    scenario: LeadGenScenarios.PROFILE_LOW_SUPPLY,
  })

  useEffect(() => {
    if (
      !!timeZoneFetchedFromLocation &&
      !fetchTimeZoneLoading &&
      isSupportOrThirdPartyUser &&
      !readTimezone()
    ) {
      updateTimezone(timeZoneFetchedFromLocation)
    }
  }, [
    timeZoneFetchedFromLocation,
    fetchTimeZoneLoading,
    isSupportOrThirdPartyUser,
    updateTimezone,
  ])

  useEffect(() => {
    if (!slotsLoading && !pageHasLoaded && startSlotDate) {
      const startOfTheDaySlotDate = set(new Date(startSlotDate), {
        hours: 0,
        minutes: 0,
        seconds: 0,
      })

      const providerDateSlots = providerSlots[id]
      const sortedDateKeys = providerDateSlots
        ? Object.keys(providerDateSlots).sort()
        : []
      const firstDateWithSlots = sortedDateKeys.find(
        (dateKey) => providerDateSlots?.[dateKey]?.length > 0
      )

      const hasFutureSlots = getHasFutureSlots(
        nextAvailabilityForProviders || {}
      )

      let targetSlotDate: Date | null = null

      if (firstDateWithSlots) {
        const [year, month, day] = firstDateWithSlots.split('-').map(Number)
        targetSlotDate = new Date(year, month - 1, day, 0, 0, 0)
      } else if (hasFutureSlots && nextAvailabilityForProviders?.[id]?.start) {
        targetSlotDate = set(new Date(nextAvailabilityForProviders[id].start), {
          hours: 0,
          minutes: 0,
          seconds: 0,
        })
      }

      if (targetSlotDate) {
        const days = differenceInCalendarDays(
          targetSlotDate,
          startOfTheDaySlotDate
        )

        if (days >= stickyVisibleDayCount) {
          const daysToStartOfDateRange = days % stickyVisibleDayCount
          const daysAdjusted = days - daysToStartOfDateRange
          const nextAvailableDateRangeStart = addDays(
            startOfTheDaySlotDate,
            daysAdjusted
          )

          setStartSlotDate(nextAvailableDateRangeStart)
        }
      }

      setPageHasLoaded(true)
    }
  }, [slotsLoading, pageHasLoaded, startSlotDate, stickyVisibleDayCount, id])

  useEffect(() => {
    if (
      startSlotDate &&
      providerSlotsCountShown > 0 &&
      !slotsLoading &&
      appointmentMode &&
      user
    ) {
      providerRudderStackEvent(
        RudderEvent.PROVIDER_PROFILE_PAGE_AVAILABILITY_SHOWN,
        providerDetails,
        {
          count_provider_slots_shown: providerSlotsCountShown,
          payment_type_filter: getSelectedPaymentType(formattedRouterQuery),
          account_id: user?.id,
          counseling_type: counselingTypeId,
          appointment_type: appointmentMode,
          trigger_for_refresh: renderReason,
          ...(isPrimaryBookButtonV2Enabled && {
            variant: PRIMARY_BOOK_BUTTON_TRACKING.VARIANT_LABEL,
          }),
        }
      )
    }
  }, [
    slotsLoading,
    providerSlotsCountShown,
    counselingSelection,
    appointmentMode,
    isPrimaryBookButtonV2Enabled,
    renderReason,
    user,
  ])

  useEffect(() => {
    // counts the amount of available slots to send over to RudderStack
    const totalSlots = Object.values(generatedDatedSlots).flat().filter(Boolean)
      .length

    if (pageHasLoaded && !slotsLoading && !registeredRDSEvent.current) {
      onCalculateSlots?.(totalSlots)
      registeredRDSEvent.current = true
    }
  }, [generatedDatedSlots, slotsLoading, pageHasLoaded])

  const showLeadGenElements = isLowAvailability

  return (
    <>
      <div
        ref={cardRef}
        style={
          isStickyFeasible
            ? containerStyle
            : { ...containerStyle, position: 'static', top: 'auto' }
        }
        className={styles.booking_section_container}
      >
        <If condition={!isSupportOrThirdPartyUser}>
          <h2 className={styles.booking_section_header}>
            Book your {isAuthenticated ? '' : 'first'} session
          </h2>
        </If>

        <If condition={isSupportOrThirdPartyUser}>
          <div className={styles.timezone_picker_section}>
            <div>Client&apos;s session will be booked in</div>
            <TimeZonePicker
              containerClass={styles.timezone_picker_container}
              showClientMsgHint={false}
            />
          </div>
        </If>

        <div className={styles.booking_section_content}>
          <AppointmentDropdown
            counselingSelection={counselingSelection}
            handleCounselingSelection={handleCounselingSelection}
            providerDetails={providerDetails}
            isEnabled={!isPrimaryBookButtonConfirming}
          />
          <VisitTypeCheckbox
            handleAppointmentMode={handleAppointmentMode}
            providerName={providerDetails.firstName}
            appointmentMode={appointmentMode}
            availabilityLabels={availabilityLabels}
            userSelectedAvailType={userSelectedAvailType}
            containerClass={styles.session_type_checkbox}
            showTitle={false}
            onProfilePage={true}
            isEnabled={!isPrimaryBookButtonConfirming}
          />
          <div>
            <DatePaginator
              selectedDate={startSlotDate}
              onDateChange={handleDateChange}
              className={styles.date_paginator_container}
              lowerDateFormat="MONTH_DATE_FORMAT"
              dateColumnClass={styles.date_paginator_column}
              visibleDateRangeLimit={stickyVisibleDayCount}
              extendedBookingEnabled={isExtendedBookingEnabled}
            />
            <SlotGrid
              className={styles.slot_grid_container}
              providerDetails={providerDetails}
              dates={dates}
              showSlotSelection={isPrimaryBookButtonV2Enabled}
              selectedSlot={primaryBookButtonEffectiveSlot}
              generatedSlots={generatedDatedSlots}
              onSlotClick={
                isPrimaryBookButtonV2Enabled
                  ? (slot) => {
                      trackSlotSelected(slot, 'normal')
                      handlePrimaryBookButtonSlotClick(slot)
                    }
                  : (slot) => {
                      trackSlotSelected(slot, 'normal')
                      bookAppointment(slot)
                    }
              }
              slotGridButtonClass={styles.slot_grid_button}
              emptySlotClass={styles.empty_slot_button}
              showLimitedSlots
              showSlotAvailabilityIcon
              appointmentMode={appointmentMode}
              isProviderPage
              loadingSlots={slotsLoading || !pageHasLoaded}
              loadingSlotNumber={3}
              errorMessage={slotsError?.message}
              maxVisibleRowLimit={
                isPrimaryBookButtonV2Enabled ? visibleSlotRows : undefined
              }
            />
          </div>

          <If
            condition={
              isPrimaryBookButtonV2Enabled && !isSupportOrThirdPartyUser
            }
          >
            <div className={styles.primary_book_button_divider} />
            <div>
              <PrimaryBookButton
                slot={primaryBookButtonEffectiveSlot?.slot}
                format={format}
                timeZone={timeZone}
                onBook={handlePrimaryBookButtonConfirm}
                isSlotSelected={isPrimaryBookButtonSlotSelected}
                isLoading={
                  isPrimaryBookButtonConfirming ||
                  slotsLoading ||
                  !pageHasLoaded
                }
              />
            </div>
          </If>

          <Choose>
            <When condition={showLeadGenElements}>
              <Typography
                tag="p"
                family="Gibson"
                className="~mt-5 ~mb-6 ~w-full ~text-center"
              >
                Can&#39;t find a time that works?{' '}
                <LinkElement forwardingFilters={forwardingFilters}>
                  Get availability alerts
                </LinkElement>
              </Typography>
            </When>
            <Otherwise>
              <Button
                variant="link"
                onClick={() => openProviderProfileShowAllSlotsInUrl(router)}
                className={classNames(
                  styles.view_availability_btn,
                  styles.view_availability_btn_spec,
                  CTA_CLASSES.viewAllAvailability
                )}
              >
                View all availability
              </Button>
            </Otherwise>
          </Choose>

          <If condition={!isSupportOrThirdPartyUser}>
            <div className={classNames(styles.booking_help_text)}>
              Want help booking?&nbsp;
              <PhoneNumberLink
                href={`tel:${phoneNumber}`}
                className={`~no-underline ${CTA_CLASSES.callUs}`}
                rel="nofollow"
                phoneNumberCTA={formattedPhoneNumber}
              >
                Call us at{' '}
                <span className={styles.font_weight_semi_bold}>
                  {formattedPhoneNumber}
                </span>
              </PhoneNumberLink>
              {!isAuthenticated && (
                <div>
                  <span className={styles.existing_client_text}>
                    Existing client?
                  </span>{' '}
                  <Button
                    variant="link"
                    onClick={handleLogin}
                    className={styles.login_btn}
                  >
                    Log in
                  </Button>{' '}
                  <span className={styles.rebook_text}>and rebook</span>
                </div>
              )}
            </div>
          </If>
        </div>
      </div>
    </>
  )
}

export default withErrorBoundary(withTimezoneInitialized(StickyScheduleSection))
